/**
 * Data provider для Refine.
 *
 * Обёртка над @refinedev/simple-rest с авто-добавлением Bearer-токена.
 * Все запросы идут на /api/v1/* — nginx маршрутизирует на FastAPI бэкенд.
 *
 * Особенности нашего API:
 * - Списки возвращаются как `[]`, не как `{data, total, ...}` → используем
 *   simple-rest, но без headers x-total-count.
 * - PATCH работает на /transactions/{id} — Refine это понимает по умолчанию.
 * - Для категоризации используем кастомный custom() метод.
 */
import dataProviderSimpleRest from "@refinedev/simple-rest";
import axios from "axios";
import { getToken, clearToken } from "./authProvider";

export const API_BASE = "/api/v1";

const axiosInstance = axios.create();

// Интерцептор: добавить Bearer-токен ко всем запросам
axiosInstance.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Интерцептор: на 401 — очистить токен и кинуть в /login
axiosInstance.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      clearToken();
      // Ленивая навигация — без react-router (мы внутри Refine, он сам обработает)
      const onLogin = window.location.pathname === "/login";
      if (!onLogin) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

// Базовый simple-rest — Refine ожидает /resource (без trailing slash для большинства),
// у нас в API /transactions/, /bank-connectors/ — со слэшем. Ниже — кастомный resolver.
export const dataProvider = dataProviderSimpleRest(API_BASE, axiosInstance);

/**
 * Утилита для произвольных запросов (не Refine-resource):
 *   await api.get('/dashboard/summary')
 *   await api.post('/profit-first/recalc')
 */
export const api = {
  get: async <T = unknown>(path: string, params?: Record<string, unknown>): Promise<T> => {
    const resp = await axiosInstance.get<T>(`${API_BASE}${path}`, { params });
    return resp.data;
  },
  post: async <T = unknown>(path: string, body?: unknown): Promise<T> => {
    const resp = await axiosInstance.post<T>(`${API_BASE}${path}`, body);
    return resp.data;
  },
  patch: async <T = unknown>(path: string, body?: unknown): Promise<T> => {
    const resp = await axiosInstance.patch<T>(`${API_BASE}${path}`, body);
    return resp.data;
  },
  delete: async <T = unknown>(path: string): Promise<T> => {
    const resp = await axiosInstance.delete<T>(`${API_BASE}${path}`);
    return resp.data;
  },
};
